import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Phone,
  Sparkles,
  Shield,
  Eye,
  AlertCircle,
  CheckCircle,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";

const MaintenancePage = () => {
  const cleaningBenefits = [
    {
      icon: Sparkles,
      title: "Redonner le fini brillant",
      description: "Retrouver l'éclat d'origine de votre prothèse pour un sourire lumineux."
    },
    {
      icon: Shield,
      title: "Éliminer les taches",
      description: "Retirer les dépôts et colorations qui s'accumulent avec le temps."
    },
    {
      icon: Eye,
      title: "Vérifier la santé des tissus buccaux",
      description: "Examiner l'état de vos gencives et détecter tout problème potentiel."
    },
    {
      icon: AlertCircle,
      title: "Prévenir l'inflammation et la perte osseuse",
      description: "Un entretien régulier aide à maintenir la santé de votre bouche."
    }
  ];

  const dailyCareTips = [
    "Rincez votre prothèse après chaque repas",
    "Brossez-la délicatement avec une brosse à prothèse",
    "Utilisez un nettoyant spécialement conçu pour les prothèses",
    "Laissez-la tremper la nuit dans une solution nettoyante",
    "Manipulez-la au-dessus d'un évier rempli d'eau ou d'une serviette"
  ];

  return (
    <div className="bg-[#F5F5F0]" data-testid="maintenance-page">
      {/* Hero Section */}
      <section className="bg-white section-padding" data-testid="maintenance-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-playfair heading-xl text-slate-900 mb-6">
              Prendre soin de votre prothèse
            </h1>
            <p className="text-body-lg">
              Un entretien régulier de votre prothèse est essentiel pour préserver 
              sa qualité, votre confort et la santé de votre bouche.
            </p>
          </div>
        </div>
      </section>

      {/* Annual Cleaning */}
      <section className="section-padding" data-testid="maintenance-cleaning">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-full mb-6">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Recommandation annuelle</span>
            </div>
            <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
              Nettoyage et polissage professionnels
            </h2>
            <p className="text-body-lg max-w-2xl mx-auto">
              Un nettoyage et un polissage annuels permettent de maintenir 
              votre prothèse en excellent état.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cleaningBenefits.map((benefit, index) => (
              <div
                key={index}
                className="card p-8 hover:shadow-lg transition-shadow"
                data-testid={`cleaning-benefit-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="icon-circle bg-[#F5F5F0] flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="heading-playfair text-xl font-semibold mb-3 text-slate-900">
                      {benefit.title}
                    </h3>
                    <p className="text-body">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Care */}
      <section className="bg-white section-padding" data-testid="maintenance-daily">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="heading-playfair heading-lg text-slate-900">
                Conseils d'entretien quotidien
              </h2>
              <p className="text-body-lg">
                En plus du nettoyage professionnel annuel, quelques gestes simples 
                au quotidien vous aideront à préserver votre prothèse.
              </p>
              <ul className="space-y-4">
                {dailyCareTips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-body">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="hero-image aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1642844819197-5f5f21b89ff8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Intérieur d'une clinique dentaire moderne"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dentist Recommendation */}
      <section className="section-padding" data-testid="maintenance-dentist">
        <div className="container-custom">
          <div className="card p-8 md:p-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="icon-circle mx-auto mb-6 bg-[#F5F5F0]">
                <Shield className="w-6 h-6 text-slate-900" />
              </div>
              <h2 className="heading-playfair heading-md text-slate-900 mb-4">
                Suivi dentaire régulier
              </h2>
              <p className="text-body-lg max-w-2xl mx-auto">
                Nous recommandons également un suivi régulier chez le dentiste 
                pour la santé globale de la bouche. Votre dentiste et votre 
                denturologiste travaillent ensemble pour assurer votre bien-être oral.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white section-padding" data-testid="maintenance-cta">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="heading-playfair text-2xl md:text-3xl text-white mb-6">
              Planifiez votre entretien annuel
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Contactez-nous pour prendre rendez-vous pour le nettoyage et 
              le polissage de votre prothèse. Nous vérifierons également 
              l'état de vos tissus buccaux.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="maintenance-cta-appointment">
                <Button className="btn-call text-base px-8 py-6">
                  Prendre rendez-vous
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a 
                href="tel:450-441-3636" 
                className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-slate-900 text-base px-8 py-6"
                data-testid="maintenance-cta-call"
              >
                <Phone className="w-5 h-5" />
                <span>450-441-3636</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MaintenancePage;
